import { motion, MotionConfig } from "framer-motion";
import {
  Database,
  Activity,
  Brain,
  Layers,
  RefreshCw,
  TrendingUp,
  ArrowRight,
} from "lucide-react";
import { useInViewOnce } from "../../../hooks/useInViewOnce";

const SPRING: [number, number, number, number] = [0.34, 1.56, 0.64, 1];

const STEPS = [
  {
    num: "01",
    icon: Database,
    label: "Centralizar",
    title: "Todos os dados, um só lugar",
    description:
      "Matrículas, turmas, notas, frequência e comunicados saem das planilhas e chegam a um sistema único — acessível por papel e perfil.",
    outcome: "Base unificada",
  },
  {
    num: "02",
    icon: Activity,
    label: "Enxergar",
    title: "Desempenho em tempo real",
    description:
      "Coordenadores e professores acompanham frequência, médias e evolução por turma e por aluno — sem esperar o fim do bimestre.",
    outcome: "Visibilidade total",
  },
  {
    num: "03",
    icon: Brain,
    label: "Diagnosticar",
    title: "IA mapeia onde está a lacuna",
    description:
      "O motor de IA analisa padrões de erro e identifica onde cada aluno precisa de reforço — antes que o problema vire acúmulo.",
    outcome: "Diagnóstico preciso",
  },
  {
    num: "04",
    icon: Layers,
    label: "Personalizar",
    title: "Plano adaptado ao perfil",
    description:
      "O perfil de aprendizagem de cada aluno define como o conteúdo é apresentado e o ritmo da progressão — sem alterar o currículo da escola.",
    outcome: "Trilha individual",
  },
  {
    num: "05",
    icon: RefreshCw,
    label: "Praticar",
    title: "Revisão que fecha a lacuna",
    description:
      "Repetição espaçada e simulados adaptativos concentram o esforço nos pontos mais frágeis de cada aluno, no momento certo.",
    outcome: "Aprendizado fixado",
  },
  {
    num: "06",
    icon: TrendingUp,
    label: "Decidir",
    title: "Dados que geram ação real",
    description:
      "Com indicadores consolidados, coordenadores ajustam estratégias pedagógicas e financeiras com base em evidência — não em intuição.",
    outcome: "Gestão com dados",
  },
] as const;

export function JourneySection() {
  const { ref, isVisible } = useInViewOnce();

  return (
    <MotionConfig reducedMotion="user">
      <section
        id="historia"
        ref={ref}
        className="relative border-y border-white/[0.06] py-20 sm:py-24 lg:py-28"
        aria-label="Percurso do produto NeuroMent — seis etapas de transformação"
      >
        {/* Ambient glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(ellipse 70% 45% at 50% 0%, rgba(45,212,191,0.055) 0%, transparent 70%)",
          }}
        />

        <div className="container relative mx-auto max-w-7xl px-4 sm:px-6">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: [0, 0, 0.2, 1] }}
            className="mx-auto max-w-2xl text-center"
          >
            <span className="mb-4 inline-block rounded-full border border-teal-400/30 bg-teal-500/10 px-3 py-1 text-xs font-medium text-teal-300 sm:text-sm">
              Percurso do produto
            </span>
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl md:text-4xl">
              Da escola fragmentada ao{" "}
              <span className="bg-gradient-to-r from-teal-300 via-cyan-200 to-sky-300 bg-clip-text text-transparent">
                ERP inteligente
              </span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-400 sm:text-lg">
              Seis etapas que transformam dado bruto em decisão pedagógica —
              tudo no mesmo sistema.
            </p>
          </motion.div>

          {/* Steps grid */}
          <div className="mx-auto mt-14 grid max-w-6xl gap-4 sm:mt-16 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 lg:gap-6">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.article
                  key={step.num}
                  initial={{ opacity: 0, y: 40 }}
                  animate={isVisible ? { opacity: 1, y: 0 } : {}}
                  transition={{
                    duration: 0.65,
                    ease: SPRING,
                    delay: i * 0.08,
                  }}
                  className="group relative overflow-hidden rounded-2xl border border-white/[0.06] bg-gradient-to-b from-[#0d1525] to-[#080f1c] p-6 transition-all duration-200 hover:-translate-y-1 hover:border-teal-400/20 hover:shadow-[0_8px_40px_-12px_rgba(45,212,191,0.22)] sm:p-7"
                  aria-label={`Etapa ${step.num}: ${step.title}`}
                >
                  {/* Decorative step number */}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute -right-1 -top-2 select-none font-display text-[5.5rem] font-bold leading-none tracking-tighter text-teal-300/[0.055] sm:text-[6.5rem]"
                  >
                    {step.num}
                  </span>

                  {/* Icon + label row */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-500/10 ring-1 ring-teal-400/20 transition-colors duration-200 group-hover:bg-teal-500/15 group-hover:ring-teal-400/35">
                      <Icon className="h-5 w-5 text-teal-300" aria-hidden />
                    </div>
                    <span className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-teal-400/80">
                      {step.label}
                    </span>
                  </div>

                  {/* Heading */}
                  <h3 className="mt-5 font-display text-base font-semibold leading-snug tracking-tight text-white sm:text-[1.05rem]">
                    {step.title}
                  </h3>

                  {/* Description */}
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-400">
                    {step.description}
                  </p>

                  {/* Outcome chip */}
                  <div className="mt-5 flex items-center gap-2 border-t border-white/[0.05] pt-4">
                    <span
                      aria-hidden
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-teal-400/60"
                    />
                    <span className="text-xs font-medium text-teal-300/75">
                      {step.outcome}
                    </span>
                  </div>
                </motion.article>
              );
            })}
          </div>

          {/* Closing connector */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.62, duration: 0.5, ease: [0, 0, 0.2, 1] }}
            className="mt-12 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm text-slate-500 sm:mt-14"
          >
            <span>Cada etapa alimenta a próxima</span>
            <ArrowRight
              className="h-3.5 w-3.5 shrink-0 text-teal-400/60"
              aria-hidden
            />
            <span className="font-medium text-teal-400/80">
              o NeuroMent cresce com a escola
            </span>
          </motion.div>
        </div>
      </section>
    </MotionConfig>
  );
}
